// src/views/Seguridad/Permisos/FiltroPermisos.jsx
import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";

export default function FiltroPermisos({
  initial = { moduloId: "" },
  onSubmit = () => {},
  onClose = () => {},
}) {
  const [moduloId, setModuloId] = useState(initial.moduloId || "");

  // ⚙️ Estado de carga de módulos
  const [modulos, setModulos] = useState([]); // [{ value: MENU_SQ_COD, label: MENU_DS_TITULO }]
  const [loadingMods, setLoadingMods] = useState(false);
  const [errorMods, setErrorMods] = useState(null);
  const acRef = useRef(null); // abort controller

  // 🔄 Cargar módulos (MENU_ITEM nivel=2) apenas se monte el panel
  useEffect(() => {
    if (acRef.current) {
      acRef.current.abort();
      acRef.current = null;
    }
    const controller = new AbortController();
    acRef.current = controller;

    (async () => {
      setLoadingMods(true);
      setErrorMods(null);
      try {
        // Traemos páginas hasta que hasMore sea false (son pocos, pero quedamos cubiertos)
        let page = 0;
        const size = 200;
        const acc = [];

        // Nota: el endpoint responde items con las claves exactas de DB:
        // { "MENU_SQ_COD": <id>, "MENU_DS_TITULO": "<titulo>" }
        // y además {hasMore, page, size}
        // Vamos paginando hasta completar todo.
        // Si preferís una sola página grande, dejá solo una llamada con size alto.
        // Uso del helper:
        //   apiRoutes.permisos.autocompleteModuloUrl(q="", page, size)
        // No usamos 'q' para módulo.
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const url = apiRoutes.permisos.autocompleteModuloUrl("", page, size);
          const data = await apiFetchJson(url, { signal: controller.signal });

          const chunk =
            (data?.items || []).map((it) => ({
              value: String(it.MENU_SQ_COD ?? ""), // valor del option
              label: String(it.MENU_DS_TITULO ?? ""), // texto del option
            })) ?? [];

          acc.push(...chunk);

          const hasMore = !!data?.hasMore;
          if (!hasMore) break;
          page = (data?.page ?? page) + 1;
        }

        // Ordenamos por label por las dudas (el backend ya manda order ASC por id)
        acc.sort((a, b) => a.label.localeCompare(b.label, "es"));
        setModulos(acc);
      } catch (e) {
        if (!(e?.name === "AbortError")) {
          console.error("Error cargando módulos:", e);
          setErrorMods(e);
          setModulos([]);
        }
      } finally {
        if (!controller.signal.aborted) setLoadingMods(false);
      }
    })();

    return () => controller.abort();
  }, []);

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      // El backend espera "modulo" (string). Usamos el id del MENU_ITEM nivel=2.
      modulo: (moduloId || "").toString().trim(),
    });
    onClose();
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div className="drawer-panel overflow-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="drawer-header">
          <h2 className="text-title">Filtros</h2>
          <button onClick={onClose} className="btn-cancel" title="Cerrar">
            &times;
          </button>
        </div>

        {/* Body */}
        <form onSubmit={submit} className="drawer-body">
          {/* Módulo */}
          <div className="relative">
            <label className="form-label-fil" htmlFor="moduloSel">
              Módulo
            </label>

            <select
              id="moduloSel"
              className="input"
              value={moduloId}
              onChange={(e) => setModuloId(e.target.value)}
              disabled={loadingMods || !!errorMods}
            >
              <option value="">Todos</option>
              {modulos.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>

            {loadingMods && (
              <div className="text-sm text-gray-500 mt-1">Cargando módulos…</div>
            )}
            {!loadingMods && errorMods && (
              <div className="text-sm text-red-600 mt-1">
                Ocurrió un error al cargar los módulos.
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="text-right">
            <button type="submit" className="btn">Aplicar Filtros</button>
          </div>
        </form>
      </div>
    </div>
  );
}
